import React, { useEffect, useRef, useState } from 'react';
import { BRAND } from '../../branding';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useQueryClient } from '@tanstack/react-query';
import TuneIcon from '@mui/icons-material/Tune';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ForumIcon from '@mui/icons-material/Forum';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RouteIcon from '@mui/icons-material/Route';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PercentIcon from '@mui/icons-material/Percent';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import HomeIcon from '@mui/icons-material/Home';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SellIcon from '@mui/icons-material/Sell';
import SpeedIcon from '@mui/icons-material/Speed';
import { useAuth } from '../../hooks/useAuth';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import userPreferencesService, {
  type UserPreferences,
} from '../../services/userPreferencesService';
import { getTodayString } from '../../utils/timeUtils';
import { Phoxy } from '../Phoxy';
import { ReleaseNotesModal } from './ReleaseNotesModal';

const BRAND_DARK = '#0a5740';
const BRAND_DARKER = '#073c2c';
const BRAND_ACCENT = '#4dd693';

interface Highlight {
  icon: React.ReactNode;
  title: string;
  description: string;
}

export interface ReleaseAnnouncement {
  /** Format `YYYY-MM-DD-vX_Y_Z` — release date + top version. Keys the dismissal map. */
  id: string;
  /** e.g. 'V2.3.465 → V2.3.471' — version before the release → top version after. */
  versionRange: string;
  dateLabel: string;
  /** Not shown automatically after this date (YYYY-MM-DD) — late logins skip stale news. */
  expires: string;
  heroSubline: string;
  highlights: Highlight[];
}

/**
 * One-time "what shipped" announcements shown after login. Newest first —
 * only the FIRST entry auto-shows; older ones stay replayable from
 * Profile → My Settings. Seen-state lives in TWO places:
 *   1. user_preferences.workflow_defaults.dismissedAnnouncements (DB — the
 *      source of truth; survives cache clears and follows the user across
 *      machines), keyed by announcement id, and
 *   2. localStorage (fast path so returning users never wait on the API).
 * To announce a future release, prepend a new entry here (its new id is what
 * re-arms the popup for everyone) — see how-to/HOW_TO_UPDATE_RELEASE_ANNOUNCEMENT.md.
 */
export const RELEASE_ANNOUNCEMENTS: ReleaseAnnouncement[] = [
  {
    id: '2026-08-20-v2_3_534',
    versionRange: 'V2.3.516 → V2.3.534',
    dateLabel: 'August 20, 2026',
    expires: '2026-10-01',
    heroSubline:
      'Kroll documents inside Patient 360, texts patients can answer with a word, verification progress that saves itself, and delivery territories that know your postal codes — the tour of eighteen releases.',
    highlights: [
      {
        icon: <MedicalInformationIcon fontSize="small" />,
        title: 'Patient documents, straight from Kroll',
        description:
          'Patient 360’s Documents tab now shows the patient’s Kroll documents with preview — import the ones you need, upload new ones, and pin the important ones to the top.',
      },
      {
        icon: <FactCheckIcon fontSize="small" />,
        title: 'Product Verification keeps your progress',
        description:
          'Each drug’s check is saved as you verify it — Save for Later and come back without losing anything — and a per-pharmacy setting can require a second person for the final check.',
      },
      {
        icon: <ForumIcon fontSize="small" />,
        title: 'Texts patients can answer with a word',
        description:
          'Confirmation texts ask for YES or NO instead of numbered menus, duplicate messages are gone, and the Communication Center link in the sidebar shows how many replies are waiting.',
      },
      {
        icon: <RouteIcon fontSize="small" />,
        title: 'Delivery territories know your postal codes',
        description:
          'Territory Manager resolves addresses by postal code (FSA) first and falls back to the city, so out-of-town patients pick up the right delivery days and method automatically.',
      },
      {
        icon: <LocalShippingIcon fontSize="small" />,
        title: 'Return to Pharmacy, with a scan-in gate',
        description:
          'Products coming back to the shelf are scanned back in before the order can be cancelled — both halves of a fridge split included — so stock counts stay honest.',
      },
      {
        icon: <SellIcon fontSize="small" />,
        title: 'Labels, receipts and hardcopies match Kroll',
        description:
          'Discounted prices print correctly, hardcopies show real phone numbers and statuses, and vial labels carry the prescriber’s actual designation — Dr., NP, or RPh.',
      },
    ],
  },
  {
    id: '2026-08-07-v2_3_516',
    versionRange: 'V2.3.502 → V2.3.516',
    dateLabel: 'August 7, 2026',
    expires: '2026-09-18',
    heroSubline:
      'Nursing homes get their own workflow, the app gets faster, and six weeks of your reports are fixed. Here’s the tour.',
    highlights: [
      {
        icon: <HomeIcon fontSize="small" />,
        title: 'Nursing-home orders, end to end',
        description:
          'A third order type next to Delivery and Pickup: no courier, no patient texts, finished at the Product Verification scan. A Nursing Home tab groups orders by home, the home’s name shows everywhere — queues, search, the delivery tag — and billing can record AR – Group Home.',
      },
      {
        icon: <FactCheckIcon fontSize="small" />,
        title: 'Follow-up dates you don’t have to correct',
        description:
          'The pharmacist’s CV-verified days supply drives the follow-up date (not the billed value), calculated dates skip weekends and holidays, and PRN items no longer demand a date or sit red in the queue.',
      },
      {
        icon: <SpeedIcon fontSize="small" />,
        title: 'The whole app is faster',
        description:
          'A runaway Clinical Verification refresh loop is gone, API responses are compressed, the dashboard and busiest grids render lighter — and compact queue headers put more orders on screen.',
      },
      {
        icon: <ForumIcon fontSize="small" />,
        title: 'One patient, one SMS number',
        description:
          'Every text resolves to the patient’s single designated number — if it can’t be established, the message is held for staff instead of guessed. Pickup orders send pickup wording, and the delivery-confirmation text is back to its clear reply-NO format.',
      },
      {
        icon: <AssignmentTurnedInIcon fontSize="small" />,
        title: 'Reimbursement Tracker grows up',
        description:
          'Per-insurer coverages with their own approval and expiry dates, new statuses, RWAM, a Core/SRT field, a Last Updated By column, and a grid that remembers your column layout.',
      },
      {
        icon: <AutoAwesomeIcon fontSize="small" />,
        title: '…and thirty more fixes',
        description:
          'Single-scan Product Verification, 17 Clinical Verification troubleshooting reasons, a readable Hard Copy pane, per-claim GP % with a low-GP gate, IV clinical notes and printable delivery confirmations, phone/DOB search, and the subscription wizard phone fix.',
      },
    ],
  },
  {
    id: '2026-07-21-v2_3_502',
    versionRange: 'V2.3.486 → V2.3.502',
    dateLabel: 'July 21, 2026',
    expires: '2026-09-01',
    heroSubline:
      'A fuller patient picture and money views you can trust. Here’s the tour.',
    highlights: [
      {
        icon: <MedicalInformationIcon fontSize="small" />,
        title: 'Patient 360 now records weight and the full med list',
        description:
          'The Add / Manage dialog gains a weight field and a medication list (BPMH), so allergies, conditions, weight, and meds are all visible at a glance for clinical assessment.',
      },
      {
        icon: <SellIcon fontSize="small" />,
        title: 'Per-drug due dates on Process Billing',
        description:
          'Each drug on the Select Medication step shows its next-fill date as the same colour-coded chip as the queue — decide fill-or-skip without leaving the modal.',
      },
      {
        icon: <HighlightOffIcon fontSize="small" />,
        title: 'Awaiting Payment shows only real payables',
        description:
          'Cancelled orders — including cancelled troubleshooting copies — no longer appear in Billed - Awaiting Payment, and the tab counts and totals match the list.',
      },
      {
        icon: <LocalShippingIcon fontSize="small" />,
        title: 'Missed deliveries stay open until really delivered',
        description:
          'The overnight remediation job now checks the carrier’s latest event — an order whose delivery was missed is left for rescheduling instead of being auto-marked "Delivery Completed".',
      },
      {
        icon: <AutoAwesomeIcon fontSize="small" />,
        title: '…and more fixes',
        description:
          'Clinical reasons in the Billing Troubleshooting dropdown, a faster Ready to Ship queue, counseling entries you can remove and re-date, a Create Order wizard that advances on the first click, shared-Rx cancellation from Clinical Verification, and real dollar amounts on the Pre-Bill report.',
      },
    ],
  },
  {
    id: '2026-07-16-v2_3_486',
    versionRange: 'V2.3.471 → V2.3.486',
    dateLabel: 'July 16, 2026',
    expires: '2026-08-31',
    heroSubline:
      'A big batch of your reports fixed in one go — plus a brand-new home for allergies. Here’s the quick tour.',
    highlights: [
      {
        icon: <MedicalInformationIcon fontSize="small" />,
        title: 'Allergies & conditions live on Patient 360',
        description:
          'Add and edit allergies and medical conditions right on the patient file — suggestions appear as you type, using the same standard terms Kroll uses — and allergies follow you into clinical verification and counseling.',
      },
      {
        icon: <HighlightOffIcon fontSize="small" />,
        title: 'Cancel or reschedule without leaving CV',
        description:
          'Pharmacists can cancel an order (Kroll-checked first), cancel just the delivery, or reschedule it in place — and removing a single Rx from a multi-drug order now walks through the full cancellation flow.',
      },
      {
        icon: <FactCheckIcon fontSize="small" />,
        title: 'An Order Timeline that explains itself',
        description:
          'Cancellation reasons appear in plain words instead of raw codes, automated steps say "PHOX (Automated)", a patient\'s text confirmation is credited to the patient, and cancellations record who did them.',
      },
      {
        icon: <HomeIcon fontSize="small" />,
        title: 'Address clean-up works again',
        description:
          'Extra patient addresses can be deleted — only the last active address is protected, and a blocked delete now tells you exactly why.',
      },
      {
        icon: <LocalShippingIcon fontSize="small" />,
        title: 'Delivery Remediation counts only the real backlog',
        description:
          'Today\'s active delivery run is no longer flagged as "stuck", and completions recorded without an order number can now be matched and closed with the driver\'s real delivery date.',
      },
      {
        icon: <SellIcon fontSize="small" />,
        title: 'Vial labels that admit a short fill',
        description:
          'Partial fills print "1 of 3" with a bold BALANCE OWING line, and the catch-up shipment labels itself as the balance.',
      },
      {
        icon: <AutoAwesomeIcon fontSize="small" />,
        title: '…and more fixes',
        description:
          'No more surprise sign-outs mid-shift, duplicate subscription lines removable one at a time, subscription editing behaves, one livelier Help Center — and our mascot now has a name: Phoxy.',
      },
    ],
  },
  {
    id: '2026-07-14-v2_3_471',
    versionRange: 'V2.3.465 → V2.3.471',
    dateLabel: 'July 14, 2026',
    expires: '2026-08-31',
    heroSubline:
      'A fresh batch of fixes from your feedback just landed — here’s the quick tour.',
    highlights: [
      {
        icon: <ViewWeekIcon fontSize="small" />,
        title: 'Arrange billing columns your way',
        description:
          'Drag the Billing Queue column headers into any order — the layout saves to your profile and follows you to every machine.',
      },
      {
        icon: <PercentIcon fontSize="small" />,
        title: 'GP% for every prescription',
        description:
          'The billing Payment step now shows a color-coded GP% per drug on multi-drug orders, so the one that needs rebilling stands out instantly.',
      },
      {
        icon: <ForumIcon fontSize="small" />,
        title: 'Winnipeg texts that commit to a window',
        description:
          'Winnipeg 3rd-party shipment texts no longer hedge with "afternoon or evening" — pick the delivery window before the message goes out.',
      },
      {
        icon: <HighlightOffIcon fontSize="small" />,
        title: 'Cancel right from Clinical Verification',
        description:
          'Pharmacists can cancel an order — or just its delivery — without a detour to the Scheduling Queue.',
      },
      {
        icon: <AssignmentTurnedInIcon fontSize="small" />,
        title: 'Counseling and tasks tell the truth',
        description:
          'Declined counseling shows as declined, new tasks appear the moment you create them, and counseling can be recorded right from Patient 360.',
      },
      {
        icon: <AutoAwesomeIcon fontSize="small" />,
        title: '…and fifteen more fixes',
        description:
          'IV billing troubleshooting works again, queue tab badges got their spacing back, switching to pickup texts the patient, and new starts ask the right pathway question.',
      },
    ],
  },
  {
    id: '2026-07-13-v2_3_465',
    versionRange: 'V2.3.397 → V2.3.465',
    dateLabel: 'July 13, 2026',
    expires: '2026-08-31',
    heroSubline: 'A big wave of improvements went live last night — here’s the quick tour.',
    highlights: [
      {
        icon: <TuneIcon fontSize="small" />,
        title: 'A calmer billing wizard',
        description:
          'Subscription Management now leads the medication step, every pathway carries a name that matches how you talk, and a greyed-out Next button finally tells you why.',
      },
      {
        icon: <ViewWeekIcon fontSize="small" />,
        title: 'Your queues remember you',
        description:
          'Columns, sort order, and rows-per-page now save to your profile on every queue — log in anywhere and it looks like yours. See everything saved in the new My Settings tab.',
      },
      {
        icon: <ForumIcon fontSize="small" />,
        title: 'Richer conversations',
        description:
          'Picture messages now send and receive right in the Communication Center — and stuck Failed SMS threads can finally be archived.',
      },
      {
        icon: <LocalShippingIcon fontSize="small" />,
        title: 'Deliveries that look after themselves',
        description:
          'Stuck deliveries drain automatically every hour with middle-mile recovery, and verified LtoM–MM deliveries can now be completed manually.',
      },
      {
        icon: <RouteIcon fontSize="small" />,
        title: 'The Life of an Order',
        description:
          'A new interactive Help Center guide walks the entire order journey — every stage, every scenario.',
      },
      {
        icon: <AutoAwesomeIcon fontSize="small" />,
        title: '…and a pile of polish',
        description:
          'Smarter reason lists, filters that actually filter, notes wherever you need them, and no more double scrollbars.',
      },
    ],
  },
];

export const CURRENT_RELEASE_ANNOUNCEMENT = RELEASE_ANNOUNCEMENTS[0];

/** Id of the announcement that auto-shows — kept as a named export for the gating wrapper and specs. */
export const RELEASE_ANNOUNCEMENT_ID = CURRENT_RELEASE_ANNOUNCEMENT.id;

export const releaseAnnouncementSeenKey = (
  userId: string,
  announcementId: string = RELEASE_ANNOUNCEMENT_ID,
) => `phox-release-announcement:${announcementId}:${userId}`;

export interface ReleaseAnnouncementDialogViewProps {
  open: boolean;
  onClose: () => void;
  /** When provided, shows the "Full release notes" button. */
  onOpenFullNotes?: () => void;
  /** Which release to render — defaults to the current (newest) announcement. */
  announcement?: ReleaseAnnouncement;
}

/**
 * Presentational announcement dialog — no gating or persistence. Used by the
 * auto-once wrapper below and replayed on demand (any past release) from
 * Profile → My Settings.
 */
export const ReleaseAnnouncementDialogView: React.FC<ReleaseAnnouncementDialogViewProps> = ({
  open,
  onClose,
  onOpenFullNotes,
  announcement = CURRENT_RELEASE_ANNOUNCEMENT,
}) => {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="release-announcement-title"
      data-testid="release-announcement-dialog"
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* Logo strip */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          bgcolor: 'background.paper',
        }}
      >
        <Box
          component="img"
          src={BRAND.logo}
          alt={`PHOX — ${BRAND.name}`}
          sx={{ height: 30, width: 'auto', display: 'block' }}
        />
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Release update · {announcement.dateLabel}
        </Typography>
      </Box>

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(150deg, ${BRAND_DARK}, ${BRAND_DARKER} 82%)`,
          px: 3,
          pt: 3.5,
          pb: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 88,
            height: 88,
            mx: 'auto',
            mb: 1.5,
            borderRadius: '50%',
            bgcolor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 0 0 6px ${alpha(BRAND_ACCENT, 0.25)}`,
            '@keyframes phoxFloat': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(-6px)' },
            },
            animation: prefersReducedMotion ? 'none' : 'phoxFloat 3s ease-in-out infinite',
          }}
        >
          <Phoxy size={64} />
        </Box>
        <Typography
          id="release-announcement-title"
          variant="h5"
          sx={{ color: '#eafaf1', fontWeight: 800, mb: 0.5 }}
        >
          A fresh PHOX just landed! 🎉
        </Typography>
        <Typography variant="body2" sx={{ color: '#bfe6cf', mb: 1.5 }}>
          {announcement.heroSubline}
        </Typography>
        <Chip
          label={announcement.versionRange}
          size="small"
          sx={{
            bgcolor: alpha(BRAND_ACCENT, 0.15),
            color: BRAND_ACCENT,
            fontWeight: 700,
            letterSpacing: '0.03em',
          }}
        />
      </Box>

      {/* Highlights */}
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack spacing={2}>
          {announcement.highlights.map((h) => (
            <Box key={h.title} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(BRAND_DARK, 0.08),
                  color: BRAND_DARK,
                }}
              >
                {h.icon}
              </Box>
              <Box>
                <Typography variant="body1" fontWeight={700} lineHeight={1.3}>
                  {h.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {h.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          Replay this anytime from Profile → My Settings.
        </Typography>
        {onOpenFullNotes && (
          <Button onClick={onOpenFullNotes} size="small">
            Full release notes
          </Button>
        )}
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: BRAND_DARK,
            fontWeight: 700,
            px: 3,
            '&:hover': { bgcolor: BRAND_DARKER },
          }}
        >
          Let&apos;s go!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export interface ReleaseAnnouncementDialogProps {
  /** Delay before the popup appears once gating settles (default 900ms). */
  openDelayMs?: number;
}

export const ReleaseAnnouncementDialog: React.FC<ReleaseAnnouncementDialogProps> = ({
  openDelayMs = 900,
}) => {
  const { isAuthenticated, isLoading, user, isKioskOnly } = useAuth();
  const pharmacyId = usePharmacyStore((s) => s.pharmacyId);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  // Dismissal map fetched at gate time, merged into on write so a future
  // second announcement's entry is never dropped by our PUT.
  const dismissedMapRef = useRef<Record<string, string>>({});

  const userId: string | undefined = user?.sub;

  useEffect(() => {
    if (isLoading || !isAuthenticated || !userId) return undefined;
    if (isKioskOnly()) return undefined;
    if (getTodayString() > CURRENT_RELEASE_ANNOUNCEMENT.expires) return undefined;

    let seenLocally: string | null = null;
    try {
      seenLocally = window.localStorage.getItem(releaseAnnouncementSeenKey(userId));
    } catch {
      return undefined; // storage unavailable — never risk re-showing forever
    }
    if (seenLocally) return undefined;

    let cancelled = false;
    let timer: number | undefined;

    const gate = async () => {
      // DB check — shares the ['user-preferences', pharmacyId] cache the
      // grid-settings hook populates, so a warm session costs no request.
      if (pharmacyId) {
        try {
          const prefs = await queryClient.fetchQuery<UserPreferences>({
            queryKey: ['user-preferences', pharmacyId],
            queryFn: () => userPreferencesService.getUserPreferences(pharmacyId),
            staleTime: 5 * 60_000,
          });
          const dismissed = prefs.workflowDefaults?.dismissedAnnouncements ?? {};
          dismissedMapRef.current = dismissed;
          const seenInDb = dismissed[RELEASE_ANNOUNCEMENT_ID];
          if (seenInDb) {
            try {
              window.localStorage.setItem(releaseAnnouncementSeenKey(userId), seenInDb);
            } catch {
              // fast path only — the DB flag already guarantees no re-show
            }
            return;
          }
        } catch {
          // Preferences unreachable — fall back to local-only gating rather
          // than never announcing.
        }
      }
      if (!cancelled) {
        timer = window.setTimeout(() => setOpen(true), openDelayMs);
      }
    };
    void gate();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [isLoading, isAuthenticated, userId, pharmacyId, isKioskOnly, openDelayMs, queryClient]);

  const markSeenAndClose = () => {
    const today = getTodayString();
    if (userId) {
      try {
        window.localStorage.setItem(releaseAnnouncementSeenKey(userId), today);
      } catch {
        // private mode — the DB write below still records it
      }
    }
    if (pharmacyId) {
      userPreferencesService
        .updateUserPreferences(pharmacyId, {
          workflowDefaults: {
            dismissedAnnouncements: {
              ...dismissedMapRef.current,
              [RELEASE_ANNOUNCEMENT_ID]: today,
            },
          },
        })
        .then((updated) => {
          queryClient.setQueryData(['user-preferences', pharmacyId], updated);
        })
        .catch((err) => {
          // Non-blocking: localStorage still suppresses re-shows on this
          // machine; the popup may reappear once on another device.
          console.warn('[release-announcement] failed to persist dismissal', err);
        });
    }
    setOpen(false);
  };

  const openFullNotes = () => {
    markSeenAndClose();
    setNotesOpen(true);
  };

  return (
    <>
      <ReleaseAnnouncementDialogView
        open={open}
        onClose={markSeenAndClose}
        onOpenFullNotes={openFullNotes}
      />
      <ReleaseNotesModal open={notesOpen} onClose={() => setNotesOpen(false)} />
    </>
  );
};

export default ReleaseAnnouncementDialog;
